import { useState, useCallback, useEffect } from 'react';
import api, { trackActivity, retirementScenariosAPI, portfolioRegistryAPI } from '../../../services/api';

const CURRENT_YEAR = new Date().getFullYear();

const DEFAULT_FORM_DATA = {
  couple: 'Yes',
  homeowner: 'Yes',
  birth_year_1: CURRENT_YEAR - 67,
  birth_year_2: CURRENT_YEAR - 67,
  retirement_age: 67,
  financial_assets: 1000000,
  non_financial_assets: 80000,
  investment_property_value: 0,
  employment_income: 0,
  other_income: 0,
  net_rental_income: 0,
  work_years: 0,
  other_income_years: 0,
  rental_income_years: 0,
  employment_entire_retirement: false,
  other_income_entire_retirement: false,
  rental_entire_retirement: false,
  portfolio_weights: {},
  portfolio_id: null,
  portfolio_type: null,
  income_stream: 0,
  income_stream_asset_test: 0,
  income_stream_income_test: 0,
  annuity_commencement_age: 67,
  annuity_type: 'inflation_adjusted',
  annuity_reference_portfolio: '',
  accelerated_reduction_rate: 0,
  current_portfolio_growth_exposure: 0,
  drawdown_method: '$',
  drawdown_input: 75000,
  use_sis_minimum: false,
  desired_bequest: 0,
  years: 30,
  blended_tax_rate: 0,
  annual_contributions: 0,
  lump_sum_withdrawals: [],
  step_down_withdrawals: [{ amount: 75000, fromAge: 67, toAge: 97 }],
};

// Step definitions keyed by component id
const STEP_DEFS = {
  personal:  { label: 'Personal Details',    icon: 'bi-person' },
  portfolio: { label: 'Investment Philosophy', icon: 'bi-pie-chart' },
  goals:     { label: 'Retirement Goals',     icon: 'bi-bullseye' },
};

const STEP_ORDER = ['personal', 'portfolio', 'goals'];

const useRetirementCalculator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [approach, setApproach] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [results, setResults] = useState(null);
  const [frozenScenarios, setFrozenScenarios] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [availablePortfolios, setAvailablePortfolios] = useState([]);
  const [riskUniverse, setRiskUniverse] = useState([]);   // Risk-mode universe (Strategic Series only for standard clients)
  const [riskPortfolios, setRiskPortfolios] = useState([]);
  const [objectiveOptions, setObjectiveOptions] = useState([]);
  const [portfolioRegistry, setPortfolioRegistry] = useState(null);
  const [selectedPercentileIndex, setSelectedPercentileIndex] = useState(2);

  // Database save/load state
  const [savedScenarioId, setSavedScenarioId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  const [loadedClientIdentifier, setLoadedClientIdentifier] = useState(null);
  const [isScenarioLocked, setIsScenarioLocked] = useState(false);

  // Fetch portfolios on mount
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const [availableRes, riskUniverseRes, riskRes, objectiveRes, registryRes] = await Promise.all([
          api.get('/api/retirement/available-portfolios'),
          api.get('/api/retirement/risk-universe'),
          api.get('/api/retirement/risk-portfolios'),
          api.get('/api/retirement/objective-portfolio-options'),
          portfolioRegistryAPI.getRegistry('retirement_calculator').catch(() => null),
        ]);
        // Coerce to arrays — downstream useMemos call .map/.find on these, so a
        // non-array response (transient error shape, stale bundle) must not crash render.
        const arr = (v) => (Array.isArray(v) ? v : []);
        setAvailablePortfolios(arr(availableRes.data));
        setRiskUniverse(arr(riskUniverseRes.data));
        setRiskPortfolios(arr(riskRes.data));
        setObjectiveOptions(arr(objectiveRes.data?.options));
        if (registryRes) setPortfolioRegistry(registryRes);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    };
    fetchPortfolios();
  }, []);

  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const setStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const calculate = useCallback(async () => {
    setIsCalculating(true);
    setError(null);
    try {
      // Calculate ages from birth years
      const age1Calc = CURRENT_YEAR - (formData.birth_year_1 || CURRENT_YEAR - 67);
      const age2Calc = CURRENT_YEAR - (formData.birth_year_2 || CURRENT_YEAR - 67);

      // Automatically assign older person to age_1, younger to age_2
      const isCouple = formData.couple === 'Yes';
      const age_1 = isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;
      const age_2 = isCouple ? Math.min(age1Calc, age2Calc) : age1Calc;

      // Filter out invalid lump sum entries (empty or non-numeric age)
      const cleanedFormData = {
        ...formData,
        age_1,
        age_2,
        lump_sum_withdrawals: (formData.lump_sum_withdrawals || []).filter(
          (w) => w.age !== '' && w.age !== null && !isNaN(w.age) && w.amount !== 0
        ),
      };
      const response = await api.post('/api/retirement/calculate', cleanedFormData);
      setResults(response.data);
      trackActivity('app_access', 'retirement-calculator');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setIsCalculating(false);
    }
  }, [formData]);

  const freezeScenario = useCallback(
    (name) => {
      if (!results) return;
      setFrozenScenarios((prev) => [
        ...prev,
        {
          name,
          timestamp: new Date().toISOString(),
          results: { ...results },
          formData: { ...formData },
        },
      ]);
    },
    [results, formData]
  );

  const removeScenario = useCallback((index) => {
    setFrozenScenarios((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetResults = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
    setResults(null);
    setCurrentStep(1);
    setSelectedPercentileIndex(2);
    setSavedScenarioId(null);
    setLoadedClientIdentifier(null);
    setIsScenarioLocked(false);
  }, []);

  // Save current scenario to database
  const saveToDatabase = useCallback(async (clientIdentifier, scenarioName) => {
    if (!results || !formData) return null;
    setIsSaving(true);
    setError(null);
    try {
      const saved = await retirementScenariosAPI.saveScenario(
        clientIdentifier,
        scenarioName,
        formData,
        results
      );
      setSavedScenarioId(saved.id);
      setLoadedClientIdentifier(clientIdentifier);
      return saved;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [results, formData]);

  // Load a scenario from database
  const loadFromDatabase = useCallback(async (scenarioId) => {
    setIsLoadingScenario(true);
    setError(null);
    try {
      const scenario = await retirementScenariosAPI.getScenario(scenarioId);
      setFormData(scenario.form_inputs);
      setResults(scenario.results_data);
      setSavedScenarioId(scenario.id);
      setLoadedClientIdentifier(scenario.client_identifier);
      setIsScenarioLocked(true); // Lock scenario when loaded
      setCurrentStep(3); // Go to results step
      return scenario;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setIsLoadingScenario(false);
    }
  }, []);

  // Unlock scenario for editing (will create new scenario on save)
  const unlockScenario = useCallback(() => {
    setIsScenarioLocked(false);
    setSavedScenarioId(null); // Clear ID so saving creates a new scenario
  }, []);

  const exportPdf = useCallback(
    async (chartImages, clientName) => {
      try {
        // Calculate ages from birth years
        const age1Calc = CURRENT_YEAR - (formData.birth_year_1 || CURRENT_YEAR - 67);
        const age2Calc = CURRENT_YEAR - (formData.birth_year_2 || CURRENT_YEAR - 67);

        // Automatically assign older person to age_1, younger to age_2
        const isCouple = formData.couple === 'Yes';
        const age_1 = isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;
        const age_2 = isCouple ? Math.min(age1Calc, age2Calc) : age1Calc;

        // Filter out invalid lump sum entries
        const cleanedInputs = {
          ...formData,
          age_1,
          age_2,
          lump_sum_withdrawals: (formData.lump_sum_withdrawals || []).filter(
            (w) => w.age !== '' && w.age !== null && !isNaN(w.age) && w.amount !== 0
          ),
        };
        const response = await api.post(
          '/api/retirement/export-pdf',
          {
            inputs: cleanedInputs,
            results,
            chart_images: chartImages || {},
            summary: results?.summary || '',
            selected_percentile: '50th Percentile (Median)',
            client_info: { identifier: clientName || 'Client' },
          },
          { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'retirement-report.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [formData, results]
  );

  return {
    // State
    currentStep,
    approach,
    formData,
    results,
    frozenScenarios,
    isCalculating,
    error,
    availablePortfolios,
    riskUniverse,
    riskPortfolios,
    objectiveOptions,
    portfolioRegistry,
    selectedPercentileIndex,

    // Database save/load state
    savedScenarioId,
    isSaving,
    isLoadingScenario,
    loadedClientIdentifier,
    isScenarioLocked,

    // Functions
    updateFormData,
    setStep,
    nextStep,
    prevStep,
    setApproach,
    calculate,
    freezeScenario,
    removeScenario,
    exportPdf,
    resetResults,
    setSelectedPercentileIndex,

    // Database save/load functions
    saveToDatabase,
    loadFromDatabase,
    unlockScenario,

    // Step ordering
    stepOrder: STEP_ORDER,
    stepDefs: STEP_DEFS,
  };
};

export { useRetirementCalculator };
